import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as otp from '../actions/login-confirm.actions';
import { NetworkCallService } from '../../../core/services/network-call.service';
import { Router } from '@angular/router';
import { catchError, switchMap, map } from 'rxjs/operators';
import { retryTransientHttpErrors } from '../../../core/operators/retry-transient-http-error.operator';
import { IOtpRequest, IOtpResponse } from '../../../core/models/otp.model';
import { of } from 'rxjs';
import { HelpersService } from '../../../../../src/app/core/services/helpers.service';
import { SessionStorageIds } from '../../../shared/enums';
import { AdviserLandingService } from '../../../core/services/adviser-landing.service';
import { Store } from '@ngrx/store';
import * as authActions from '../actions';
import { IBusinessUserRolesRequest } from '../models/business-user-roles.model';
import { IValidateTokenRequest } from '../models/validate-token.model';
import { EMPTY_GUID } from '../../../shared/constants';
import notify from 'devextreme/ui/notify';

@Injectable()
export class LoginConfirmEffects {
  constructor(
    private actions$: Actions,
    private networkCallService: NetworkCallService,
    private router: Router,
    private store: Store,
    private helperService: HelpersService,
    private adviserLandingService: AdviserLandingService
  ) {}

  loginConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(otp.loadLoginConfirmRequest),
      switchMap(action => {

        return this.networkCallService.post<IOtpRequest, IOtpResponse>('Authentication/businessLoginConfirm', action.request).pipe(
          map(response => {
            if (response.success && response.accessToken !== null && !response.otpExpired && !response.firstLogin) {
              sessionStorage.setItem(SessionStorageIds.USER_MOBILE, action.request.mobileNumber);
              sessionStorage.setItem(SessionStorageIds.TOKEN, response.accessToken);
              sessionStorage.setItem(SessionStorageIds.REFRESH_TOKEN, response.refreshToken);
              this.helperService.decodeJwtToken(response.accessToken);
              // Landing page depends on the roles returned by validateToken (advisers/employers open their legal entity dashboard)
              this.adviserLandingService.navigateAfterLogin();
              this.fetchAuthContext(response.accessToken);
              this.fetchBusinessRoles(response.accessToken);
            } else if (response.success && response.accessToken !== null && response.firstLogin) {
              sessionStorage.setItem(SessionStorageIds.USER_MOBILE, action.request.mobileNumber);
              sessionStorage.setItem(SessionStorageIds.TOKEN, response.accessToken);
              sessionStorage.setItem(SessionStorageIds.REFRESH_TOKEN, response.refreshToken);
              this.helperService.decodeJwtToken(response.accessToken);
              this.adviserLandingService.navigateAfterLogin();
              this.fetchAuthContext(response.accessToken);
              this.fetchBusinessRoles(response.accessToken);
            } else if (!response.success && response.accessToken === null && response.otpExpired) {
              this.helperService.notifyError('Your OTP has expired. Please resend a new OTP.', 300, 3000);
            } else if (!response.success && response.accessToken === null && response.locked) {
              this.helperService.notifyError('You have been locked out of your account. Please contact support.', 400, 3000);
            } else if (!response.success && response.accessToken === null && !response.otpExpired) {
              this.helperService.notifyError('Check your OTP and try again.', 400, 3000);
            }

            return otp.loadLoginConfirmSuccess({ response });
          }),
          retryTransientHttpErrors(),
          catchError(error => {

            return of(otp.loadLoginConfirmFailure(error));
          })
        );
        }
      )
    )
  );

  fetchAuthContext(accessToken: string) {
    const validateTokenRequest: IValidateTokenRequest = {
      token: accessToken,
      uniqueDeviceId: EMPTY_GUID
    };
    this.store.dispatch(authActions.loadValidateToken({ request: validateTokenRequest }));
  }

  fetchBusinessRoles(accessToken: string) {
    const userRolesReq: IBusinessUserRolesRequest = {
      businessId: null,
      token: accessToken,
      uniqueDeviceId: EMPTY_GUID
    };
    this.store.dispatch(authActions.loadBusinessUserRoles({ request: userRolesReq }));
  }
}
